import { MeetingNoteTense, TemplateType, TemplateExportData } from '../types';
import { UploadedTemplate } from 'src/data-capture/hooks/templates/useTemplates';
import { useState } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { Logger } from 'src/utils/Logger';
import Config from 'src/common/Config';

type CreateTemplateFromJsonPayload = {
  displayName: string;
  description: string;
  supportsMulticlient: boolean;
  templateType: TemplateType;
  previewClientId: string | null;
  sections: CreateSectionPayload[];
  meetingNoteTense: MeetingNoteTense;
};

type CreateSectionPayload = {
  populatedMarkdown: string;
  templatedMarkdown: string;
  sectionHeader: string;
  sectionHeaderLevel: number;
  preserveAsIs: boolean;
  sectionInstructions?: string;
  agent?: string;
  domainFilter?: string[];
};

export const useCreateTemplateFromJson = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<string>('');

  const createTemplateFromJson = async (
    companyId: string,
    jsonData: TemplateExportData,
    previewClientId: string | null,
  ): Promise<UploadedTemplate> => {
    setIsLoading(true);
    setProgress('Creating template from JSON...');

    const payload: CreateTemplateFromJsonPayload = {
      displayName: jsonData.templateName,
      description: jsonData.templateDescription ?? '',
      templateType: jsonData.templateType,
      supportsMulticlient: false,
      previewClientId: previewClientId,
      sections: jsonData.sections.map(section => ({
        populatedMarkdown: section.populatedMarkdown ?? section.templatedMarkdown,
        templatedMarkdown: section.templatedMarkdown,
        sectionHeader: section.sectionHeader,
        sectionHeaderLevel: section.sectionHeaderLevel,
        preserveAsIs: section.preserveAsIs,
        sectionInstructions: section.sectionInstructions,
        agent: section.agent,
        domainFilter: section.domainFilter,
      })),
      meetingNoteTense: jsonData.meetingNoteTense ?? 'THIRD_PERSON',
    };

    try {
      const response = await fetch(
        `${Config.getApiUrl()}/admin/companies/${companyId}/templates/from-json/stream`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to create template: ${response.status}`);
      }

      return await handleStreamResponse(response);
    } catch (error) {
      setIsLoading(false);
      setProgress('');
      throw error;
    }
  };

  const handleStreamResponse = async (response: Response): Promise<UploadedTemplate> => {
    if (!response.body) throw new Error('No response body');

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(new EventSourceParserStream())
      .getReader();

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done || value.data === '\n\n') break;

        if (value.data) {
          try {
            const parsed = JSON.parse(value.data);

            if (parsed.type === 'progress') {
              setProgress(parsed.data.message);
            } else if (parsed.type === 'complete') {
              setIsLoading(false);
              setProgress('');
              return parsed.data.template;
            }
          } catch (e) {
            Logger.warn('Parse error', { error: e, data: value.data });
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    throw new Error('Stream ended without completion');
  };

  return { createTemplateFromJson, isLoading, progress };
};
